import { FC, useCallback, useEffect, useState } from 'react'
import { cn } from '@bem-react/classname'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useNavigate } from 'react-router-dom'
import countryList from 'react-select-country-list'

import { Memo } from '@/hoc/Memo'
import useBackendError from '@/hooks/useBackendError'
import { useAppDispatch } from '@/core/store/hooks'
import { registrationThunk } from '@/core/services/Auth/store/auth.slice'
import { setToast } from '@/core/services/Toast/store/toast.slice'
import { ToastType } from '@/core/services/Toast/types/ToastType'
import { CountryDataModel } from '../AccountPage/models/UserModel'
import { сurrencies } from '@/config'
import { RegistrationModel } from '@/core/services/Auth/models/AuthModel'
import { getItem } from '@/utils/persistanceStorage'

import { Title } from '@/components/Title'
import { FormGroup } from '@/components/form/FormGroup'
import { Field } from '@/components/form/Field'
import { Button } from '@/components/Button'
import { Back } from '@/components/Back'
import { Checkbox } from '@/components/form/Checkbox'
import { Select } from '@/components/form/Select'

import './styles/RegistrationPage.scss'
import { Modal } from '@/components/Modal'
import useAccountValidationSchema from '@/hooks/useAccountValidationSchema'

const cnRegistration = cn('Registration')

export const RegistrationPage: FC = Memo(() => {
  const { t, i18n, ready } = useTranslation(['translation', 'countries'])
  const [setBackendError, fieldsError] = useBackendError()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const [visibleVatField, setVisibleVatField] = useState(false)
  const [successRegistrationModal, setSuccessRegistrationModal] =
    useState(false)
  const [countries, setCountries] = useState<CountryDataModel[]>([])

  useEffect(() => {
    const list = countryList().getData()
    const upd = list.map(el => {
      return {
        ...el,
        label: t(`countries:${el.value}`),
      }
    })
    if (i18n.language === 'en-GB') setCountries(list)
    else setCountries(upd)
  }, [i18n, ready])

  const RegistrationSchema = useAccountValidationSchema({
    isRegistrationForm: true,
  })

  const {
    watch,
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<RegistrationModel>({
    mode: 'onChange',
    resolver: yupResolver(RegistrationSchema),
  })

  useEffect(() => {
    const subscription = watch(value => {
      if (value.countryCodeId === 'IT') {
        setVisibleVatField(true)
      } else {
        setVisibleVatField(false)
      }
    })
    return () => subscription.unsubscribe()
  }, [watch])

  const registration = useCallback(async (data: RegistrationModel) => {
    const companyCode = getItem('companyCode', false) || null

    if (companyCode) {
      data.companyCode = companyCode
    }

    try {
      const res = await dispatch(registrationThunk(data)).unwrap()

      if (res && res.status === 200) {
        setSuccessRegistrationModal(true)
      }
    } catch (errors) {
      setBackendError(errors)
    }
  }, [])

  const onCloseModal = () => {
    dispatch(
      setToast({
        type: ToastType.success,
        message: t('You have successfully registered'),
      }),
    )
    navigate('/')
  }

  return (
    <>
      <Helmet>
        <title>{t('Sign Up')} | Registration</title>
      </Helmet>
      <div className={cnRegistration()}>
        <Back />

        <div className={cnRegistration('header')}>
          <Title className={cnRegistration('title')}>{t('Sign Up')}</Title>
          <div className={cnRegistration('text')}>
            {t(
              'Fill in the fields with information about yourself and your company',
            )}
          </div>
        </div>
        <form
          className={cnRegistration('form')}
          onSubmit={handleSubmit(registration)}
        >
          <FormGroup className={cnRegistration('grid')}>
            <Field
              {...register('name')}
              placeholder={t('Name')}
              label={t('Name')}
              required
              errors={
                (errors.name && errors.name.message) ||
                (fieldsError && fieldsError.name && fieldsError.name.message)
              }
            />

            <Field
              {...register('lastName')}
              placeholder={t('Last Name')}
              label={t('Last Name')}
              required
              errors={
                (errors.lastName && errors.lastName.message) ||
                (fieldsError &&
                  fieldsError.lastName &&
                  fieldsError.lastName.message)
              }
            />
            <Field
              {...register('companyName')}
              placeholder={t('Company Name')}
              label={t('Company Name')}
              required
              errors={
                (errors.companyName && errors.companyName.message) ||
                (fieldsError &&
                  fieldsError.companyName &&
                  fieldsError.companyName.message)
              }
            />

            <Select
              label={t('Country')}
              placeholder={t('(None)')}
              control={control}
              name='countryCodeId'
              options={countries}
              optionLabel='label'
              optionValue='value'
              required
              errors={
                (errors.countryCodeId && errors.countryCodeId.message) ||
                (fieldsError &&
                  fieldsError.countryCodeId &&
                  fieldsError.countryCodeId.message)
              }
              isNone
            />

            <Select
              label={t('Currency')}
              placeholder={t('(None)')}
              control={control}
              name='currencyId'
              options={сurrencies}
              optionLabel='name'
              optionValue='id'
              required
              errors={
                (errors.currencyId && errors.currencyId.message) ||
                (fieldsError &&
                  fieldsError.customerId &&
                  fieldsError.customerId.message)
              }
              isNone
            />

            <Field
              {...register('mobileTelephone')}
              placeholder={t('Mobile Phone')}
              label={t('Mobile Phone')}
              errors={
                (errors.mobileTelephone && errors.mobileTelephone.message) ||
                (fieldsError &&
                  fieldsError.mobileTelephone &&
                  fieldsError.mobileTelephone.message)
              }
            />
            <Field
              {...register('email')}
              placeholder={t('Email Address')}
              label={t('Email Address')}
              required
              errors={
                (errors.email && errors.email.message) ||
                (fieldsError && fieldsError.email && fieldsError.email.message)
              }
            />
            <Field
              {...register('confirmEmail')}
              placeholder={t('Confirm Email')}
              label={t('Confirm Email')}
              required
              errors={
                (errors.confirmEmail && errors.confirmEmail.message) ||
                (fieldsError &&
                  fieldsError.confirmEmail &&
                  fieldsError.confirmEmail.message)
              }
            />

            <Field
              {...register('password')}
              placeholder={t('Password')}
              label={t('Password')}
              required
              type='password'
              errors={
                (errors.password && errors.password.message) ||
                (fieldsError &&
                  fieldsError.password &&
                  fieldsError.password.message)
              }
            />
            <Field
              {...register('confirmPassword')}
              placeholder={t('Confirm Password')}
              label={t('Confirm Password')}
              required
              type='password'
              errors={
                (errors.confirmPassword && errors.confirmPassword.message) ||
                (fieldsError &&
                  fieldsError.password &&
                  fieldsError.password.message)
              }
            />
            <Field
              {...register('companyAddress')}
              placeholder={t('Company Address')}
              label={t('Company Address')}
              required
              errors={
                (errors.companyAddress && errors.companyAddress.message) ||
                (fieldsError &&
                  fieldsError.companyAddress &&
                  fieldsError.companyAddress.message)
              }
            />

            <Field
              {...register('vat')}
              placeholder={t('Vat')}
              label={t('Vat')}
              required
              errors={
                (errors.vat && errors.vat.message) ||
                (fieldsError &&
                  fieldsError.companyAddress &&
                  fieldsError.companyAddress.message)
              }
            />

            {visibleVatField && (
              <Field
                {...register('sdi')}
                placeholder={t('SDI/pec')}
                label={t('SDI/pec')}
                required
                errors={
                  (errors.sdi && errors.sdi.message) ||
                  (fieldsError &&
                    fieldsError.companyAddress &&
                    fieldsError.companyAddress.message)
                }
              />
            )}
          </FormGroup>

          <FormGroup className={cnRegistration('checkbox')}>
            <Checkbox
              {...register('privacyPolicyCheckBox')}
              labelPosition={'right'}
              label={t('I accept the registration')}
              errors={
                errors.privacyPolicyCheckBox &&
                errors.privacyPolicyCheckBox.message
              }
            />
            <a
              className={cnRegistration('privacy')}
              href='/terms'
              target='_blank'
            >
              {t('Terms of Service')}
            </a>
            <span className={cnRegistration('and')}>{t('and')}</span>
            <a
              className={cnRegistration('privacy')}
              href='/privacy'
              target='_blank'
            >
              {t('Privacy Policy')}
            </a>
          </FormGroup>
          <FormGroup className={cnRegistration('checkbox')}>
            <Checkbox
              {...register('getOffersCheckbox')}
              labelPosition={'right'}
              label={t('I agree to receive offers and promotions')}
            />
          </FormGroup>
          <Button large type='submit' isLoading={isSubmitting}>
            {t('Sign Up')}
          </Button>
        </form>
      </div>
      <Modal
        isOpen={successRegistrationModal}
        title={t(
          'Dear customer, Please follow to the email address entered during registration to pass the verification of the account. Thank you for choosing Dinamica Generale! The new Register tool will allow you manage all your cloud licenses in the easiest way.',
        )}
        titlePosition='center'
        size='md'
        onClose={() => onCloseModal()}
        isInform
        isButtonSmall
        closeBtnText='ok'
        buttonPosition='center'
      ></Modal>
    </>
  )
})
